import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { useAuth } from '@/auth/use-auth';
import { FormTextField } from '@/components/form/form-text-field';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { AuthCard } from '@/pages/auth/auth-card';
import { applyApiErrors } from '@/pages/auth/form-errors';
import { newPasswordSchema } from '@/pages/auth/password-rules';

const schema = z
  .object({
    name: z.string().min(1).max(255),
    email: z.string().email(),
    password: newPasswordSchema,
    password_confirmation: z.string().min(1),
  })
  .refine((data) => data.password === data.password_confirmation, {
    message: 'Passwords do not match',
    path: ['password_confirmation'],
  });

type RegisterValues = z.infer<typeof schema>;

export default function RegisterPage() {
  const { t } = useTranslation('auth');
  const { register } = useAuth();
  const navigate = useNavigate();

  const form = useForm<RegisterValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', email: '', password: '', password_confirmation: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    try {
      await register(values);
      toast.success(t('register.success'));
      navigate('/', { replace: true });
    } catch (error) {
      applyApiErrors(error, form.setError, (message) => toast.error(message));
    }
  });

  return (
    <AuthCard
      title={t('register.title')}
      footer={
        <span>
          {t('register.hasAccount')}{' '}
          <Link to="/login" className="text-green">
            {t('login.title')}
          </Link>
        </span>
      }
    >
      <Form {...form}>
        <form onSubmit={onSubmit} className="flex flex-col gap-4" noValidate>
          <FormTextField
            control={form.control}
            name="name"
            label={t('fields.name')}
            autoComplete="username"
            description={t('register.nameHint')}
          />
          <FormTextField
            control={form.control}
            name="email"
            label={t('fields.email')}
            type="email"
            autoComplete="email"
          />
          <FormTextField
            control={form.control}
            name="password"
            label={t('fields.password')}
            type="password"
            autoComplete="new-password"
          />
          <FormTextField
            control={form.control}
            name="password_confirmation"
            label={t('fields.passwordConfirm')}
            type="password"
            autoComplete="new-password"
          />
          <Button type="submit" variant="primary" disabled={form.formState.isSubmitting}>
            {t('register.submit')}
          </Button>
        </form>
      </Form>
    </AuthCard>
  );
}
